// Presigned-upload helper.
//
// Replaces the legacy server-streamed `/v1/documents/upload` flow with the
// three-step contract the backend now expects:
//
//   1. POST /v1/uploads/sign           → { fileId, uploadUrl, container, storageKey, expiresAt }
//   2. PUT  uploadUrl  (binary body, x-ms-blob-type: BlockBlob)
//   3. POST /v1/uploads/{fileId}/finalize  → { id, url, originalName, mimeType, size }
//
// Callers receive an envelope that exposes both the durable `fileId` (which
// owner endpoints want for submission) AND `location` / `url` (which preview
// rendering still wants until the file viewers are also migrated).
//
// Errors are surfaced through the callback via `{ location: null, error }`,
// matching the legacy contract so existing call-sites still detect failures.

import axios from './axios';
import { downscaleImage } from './downscaleImage';

// Some browsers (notably older iOS/Safari and a few Android pickers) hand back
// a File with an empty or generic `type` — HEIC/HEIF camera photos, and PDFs or
// photos picked from Drive/OneDrive/email attachments. Left as
// `application/octet-stream`, the backend rejects the sign request with 415.
// Fall back to an extension-derived MIME so those uploads still go through.
const EXT_MIME: Record<string, string> = {
  heic: 'image/heic',
  heif: 'image/heif',
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

function resolveMimeType(file: File): string {
  const type = file.type;
  if (type && type !== 'application/octet-stream') return type;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return EXT_MIME[ext] || type || 'application/octet-stream';
}

export interface UploadResult {
  fileId: number | null;
  location: string | null;
  url: string | null;
  originalName?: string;
  mimeType?: string;
  size?: number;
  error?: string;
}

export interface SignResponse {
  fileId: number;
  uploadUrl: string;
  container: string;
  storageKey: string;
  expiresAt: string;
}

export interface FinalizeResponse {
  id: number;
  url: string;
  originalName: string;
  mimeType: string;
  size: number;
}

// The legacy upload endpoint sent multipart/form-data and could land anywhere
// the server decided. The new flow needs an explicit folder, defaulting to
// `documents` since the credential / SWMS workflows that drive the bulk of
// uploads are document-shaped.
export interface UploadOptions {
  folder?: 'documents' | 'uploads' | 'images' | 'profile-images' | 'user-files' | 'company-files';
  onProgress?: (percent: number) => void;
}

async function rawAxiosPut(
  url: string,
  body: Blob | File,
  contentType: string,
  onProgress?: (percent: number) => void
): Promise<void> {
  // Use a clean axios call to bypass the app's bearer-token interceptor —
  // Azure rejects requests with an Authorization header in addition to the
  // SAS query string. axios.create(no defaults) avoids the inherited config.
  const { default: rawAxios } = await import('axios');
  await rawAxios.put(url, body, {
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': contentType || 'application/octet-stream',
    },
    // Big files: don't transform; let the browser stream the body.
    transformRequest: [(d) => d],
    onUploadProgress: onProgress
      ? (evt) => {
          const total = evt.total ?? body.size;
          if (!total) return;
          const pct = Math.min(100, Math.round((evt.loaded / total) * 100));
          onProgress(pct);
        }
      : undefined,
  });
}

export async function uploadFile(
  file: File,
  callback: (result: UploadResult) => void,
  options: UploadOptions = {}
): Promise<void> {
  if (!file) {
    callback({ fileId: null, location: null, url: null, error: 'No file provided' });
    return;
  }

  const folder = options.folder || 'documents';
  const { onProgress } = options;

  try {
    onProgress?.(0);
    // 0. Resample oversized camera photos. Everything downstream — the claimed
    //    size, the MIME we sign for, the bytes we PUT — must describe the file we
    //    actually send, so derive them after this step, not before.
    const payload = await downscaleImage(file);
    const mimeType = resolveMimeType(payload);

    // 1. Request a write SAS.
    const signResp = await axios.post<SignResponse>('/v1/uploads/sign', {
      originalName: payload.name,
      mimeType,
      size: payload.size,
      folder,
    });
    const sign = signResp.data;

    // 2. PUT the bytes directly to Azure.
    await rawAxiosPut(sign.uploadUrl, payload, mimeType, onProgress);

    // 3. Finalize on the backend — magic-byte sniff + size verification.
    const finalResp = await axios.post<FinalizeResponse>(`/v1/uploads/${sign.fileId}/finalize`);
    const finalized = finalResp.data;
    onProgress?.(100);

    callback({
      fileId: finalized.id,
      // Preserve `location` for back-compat with call-sites still reading it.
      location: finalized.url,
      url: finalized.url,
      originalName: finalized.originalName,
      mimeType: finalized.mimeType,
      size: finalized.size,
    });
  } catch (err: unknown) {
    const message =
      err instanceof Error ? err.message :
      typeof err === 'string' ? err :
      'upload failed';
    // eslint-disable-next-line no-console
    console.error('upload failed', err);
    callback({ fileId: null, location: null, url: null, error: message });
  }
}

export default uploadFile;
